// Класс для управления модальными окнами
// Полностью изолирован от React и циклов рендеринга

import { KnowledgeItem } from "../services/api";

type SetStateCallback<T> = (value: T) => void;

interface DocumentModalOptions {
  searchTerm: string;
  isLoading: boolean;
  items: KnowledgeItem[];
  onSearchChange: SetStateCallback<string>;
  onItemSelect: (item: KnowledgeItem) => void;
  onClose: () => void;
}

interface ExternalLinkOptions {
  initialUrl: string;
  onSubmit: (url: string) => void;
  onClose: () => void;
}

export class ModalManager {
  private static instance: ModalManager;
  private activeModal: HTMLDivElement | null = null;
  private updateIntervalId: number | null = null;
  private documentOptions: DocumentModalOptions | null = null;
  private externalLinkOptions: ExternalLinkOptions | null = null;
  private currentSearchTerm: string = '';
  
  // Приватный конструктор для синглтона
  private constructor() {}
  
  // Получение единственного экземпляра класса
  public static getInstance(): ModalManager {
    if (!ModalManager.instance) {
      ModalManager.instance = new ModalManager();
    }
    return ModalManager.instance;
  }
  
  // Показать модальное окно выбора документа
  public showDocumentSelector(options: DocumentModalOptions): void {
    console.log("[MODAL MANAGER] Открытие модального окна выбора документа. Элементов:", options.items.length);
    
    // Сохраняем опции
    this.documentOptions = options;
    this.externalLinkOptions = null;
    
    // Закрываем предыдущее модальное окно, если оно открыто
    this.closeModal();
    
    // Создаем новое модальное окно
    const modal = this.createDocumentSelectorModal();
    document.body.appendChild(modal);
    this.activeModal = modal;
    
    // Блокируем прокрутку основной страницы
    document.body.style.overflow = 'hidden';
    
    // Фокус на поле поиска
    setTimeout(() => {
      const searchInput = modal.querySelector('input[type="text"]');
      if (searchInput) {
        (searchInput as HTMLInputElement).focus();
      }
    }, 100);
    
    // Запускаем интервал обновления для синхронизации с состоянием React
    this.startUpdateInterval();
  }
  
  // Показать модальное окно ввода внешней ссылки
  public showExternalLinkInput(options: ExternalLinkOptions): void {
    console.log("[MODAL MANAGER] Открытие модального окна ввода внешней ссылки");
    
    // Сохраняем опции
    this.externalLinkOptions = options;
    this.documentOptions = null;
    
    // Закрываем предыдущее модальное окно, если оно открыто
    this.closeModal();
    
    // Создаем новое модальное окно
    const modal = this.createExternalLinkModal();
    document.body.appendChild(modal);
    this.activeModal = modal;
    
    // Блокируем прокрутку основной страницы
    document.body.style.overflow = 'hidden';
    
    // Фокус на поле ввода
    setTimeout(() => {
      const urlInput = modal.querySelector('input[type="url"]');
      if (urlInput) {
        (urlInput as HTMLInputElement).focus();
      }
    }, 100);
  }
  
  // Закрыть текущее модальное окно
  public closeModal(): void {
    if (this.activeModal) {
      console.log("[MODAL MANAGER] Закрытие модального окна");
      document.body.removeChild(this.activeModal);
      this.activeModal = null;
      
      // Разблокируем прокрутку основной страницы
      document.body.style.overflow = '';
      
      // Останавливаем интервал обновления
      this.stopUpdateInterval();
    }
  }
  
  // Создание модального окна для выбора документа
  private createDocumentSelectorModal(): HTMLDivElement {
    if (!this.documentOptions) throw new Error("Document options not provided");
    
    // Инициализируем локальное значение поискового запроса
    this.currentSearchTerm = this.documentOptions.searchTerm || '';
    
    // Создаем основной контейнер модального окна
    const modalContainer = document.createElement('div');
    modalContainer.id = 'modal-container';
    modalContainer.className = 'fixed inset-0 bg-black bg-opacity-40 z-[9999] flex items-center justify-center';
    
    // Обработчик клика на фоне для закрытия
    modalContainer.addEventListener('click', (e) => {
      if (e.target === modalContainer) {
        console.log("[MODAL MANAGER] Клик по фону");
        this.closeAndNotify();
      }
    });
    
    // Создаем содержимое модального окна
    const modalContent = document.createElement('div');
    modalContent.className = 'bg-white dark:bg-gray-800 rounded-lg p-4 w-96 max-w-[90vw] max-h-[90vh] z-[10000] shadow-xl';
    
    // Предотвращаем всплытие клика
    modalContent.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Заголовок
    const headerDiv = document.createElement('div');
    headerDiv.className = 'flex justify-between items-center mb-3';
    
    const title = document.createElement('h3');
    title.className = 'text-sm font-medium text-gray-700 dark:text-gray-300';
    title.textContent = 'Выберите документ для ссылки';
    
    const closeButton = document.createElement('button');
    closeButton.className = 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200';
    closeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeButton.addEventListener('click', () => {
      console.log("[MODAL MANAGER] Нажата кнопка закрытия");
      this.closeAndNotify();
    });
    
    headerDiv.appendChild(title);
    headerDiv.appendChild(closeButton);
    
    // Поле поиска
    const searchDiv = document.createElement('div');
    searchDiv.className = 'mb-3 relative';
    
    const searchIcon = document.createElement('div');
    searchIcon.className = 'absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400';
    searchIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>';
    
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.value = this.currentSearchTerm;
    searchInput.placeholder = 'Найти документ...';
    searchInput.className = 'w-full pl-10 pr-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
    
    // Обработчик ввода в поле поиска
    searchInput.addEventListener('input', (e: Event) => {
      const inputValue = (e.target as HTMLInputElement).value;
      console.log("[MODAL MANAGER] Изменение поискового запроса:", inputValue);
      
      // Сохраняем значение в локальном состоянии
      this.currentSearchTerm = inputValue;
      
      if (this.documentOptions && this.documentOptions.onSearchChange) {
        console.log("[MODAL MANAGER] Вызываем onSearchChange с значением:", inputValue);
        // Вызываем коллбэк из React для обновления состояния
        this.documentOptions.onSearchChange(inputValue);
      }
      
      // Немедленно обновляем список документов на основе нового поискового запроса
      if (this.activeModal) {
        const documentsContainer = this.activeModal.querySelector<HTMLDivElement>('#documents-container');
        if (documentsContainer) {
          this.updateDocumentsList(documentsContainer);
        }
      }
    });
    
    // Обработчик нажатия клавиш для поля поиска
    searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault(); // Предотвращаем отправку формы
        
        // Если есть результаты и первый элемент видим, выбираем его
        const firstItem = modalContent.querySelector<HTMLElement>('.document-item');
        if (firstItem) {
          firstItem.click();
        }
      }
    });
    
    searchDiv.appendChild(searchIcon);
    searchDiv.appendChild(searchInput);
    
    // Контейнер для списка документов
    const documentsContainer = document.createElement('div') as HTMLDivElement;
    documentsContainer.id = 'documents-container';
    documentsContainer.className = 'max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded mb-3';
    
    // Обновляем список документов
    this.updateDocumentsList(documentsContainer);
    
    // Кнопки в нижней части
    const buttonsDiv = document.createElement('div');
    buttonsDiv.className = 'flex justify-end';
    
    const cancelButton = document.createElement('button');
    cancelButton.className = 'px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm transition-colors';
    cancelButton.textContent = 'Отмена';
    cancelButton.addEventListener('click', () => {
      console.log("[MODAL MANAGER] Нажата кнопка Отмена");
      this.closeAndNotify();
    });
    
    buttonsDiv.appendChild(cancelButton);
    
    // Собираем все вместе
    modalContent.appendChild(headerDiv);
    modalContent.appendChild(searchDiv);
    modalContent.appendChild(documentsContainer);
    modalContent.appendChild(buttonsDiv);
    
    modalContainer.appendChild(modalContent);
    
    // Обработчик клавиши Escape
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log("[MODAL MANAGER] Нажата клавиша Escape");
        this.closeAndNotify();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Сохраняем обработчик для последующего удаления
    modalContainer.dataset.keydownHandler = 'true';
    
    // Устанавливаем фокус на поле поиска после создания модального окна
    setTimeout(() => {
      searchInput.focus();
    }, 100);
    
    return modalContainer;
  }
  
  // Создание модального окна для ввода внешней ссылки
  private createExternalLinkModal(): HTMLDivElement {
    if (!this.externalLinkOptions) throw new Error("External link options not provided");
    
    // Создаем основной контейнер модального окна
    const modalContainer = document.createElement('div');
    modalContainer.id = 'modal-container';
    modalContainer.className = 'fixed inset-0 bg-black bg-opacity-40 z-[9999] flex items-center justify-center';
    
    // Обработчик клика на фоне для закрытия
    modalContainer.addEventListener('click', (e) => {
      if (e.target === modalContainer) {
        console.log("[MODAL MANAGER] Клик по фону");
        this.closeExternalLinkModal();
      }
    });
    
    // Создаем содержимое модального окна
    const modalContent = document.createElement('div');
    modalContent.className = 'bg-white dark:bg-gray-800 rounded-lg p-4 w-96 max-w-[90vw] max-h-[90vh] z-[10000] shadow-xl';
    
    // Предотвращаем всплытие клика
    modalContent.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    
    // Заголовок
    const headerDiv = document.createElement('div');
    headerDiv.className = 'flex justify-between items-center mb-3';
    
    const title = document.createElement('h3');
    title.className = 'text-sm font-medium text-gray-700 dark:text-gray-300';
    title.textContent = 'Добавление внешней ссылки';
    
    const closeButton = document.createElement('button');
    closeButton.className = 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200';
    closeButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    closeButton.addEventListener('click', () => {
      console.log("[MODAL MANAGER] Нажата кнопка закрытия");
      this.closeExternalLinkModal();
    });
    
    headerDiv.appendChild(title);
    headerDiv.appendChild(closeButton);
    
    // Форма ввода URL
    const form = document.createElement('form');
    form.className = 'mb-3';
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const urlInput = form.querySelector('input[type="url"]') as HTMLInputElement;
      if (urlInput && this.externalLinkOptions) {
        this.externalLinkOptions.onSubmit(urlInput.value);
        this.closeModal();
      }
    });
    
    const inputDiv = document.createElement('div');
    inputDiv.className = 'mb-3';
    
    const urlLabel = document.createElement('label');
    urlLabel.className = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
    urlLabel.textContent = 'Адрес ссылки:';
    
    const urlInput = document.createElement('input');
    urlInput.type = 'url';
    urlInput.value = this.externalLinkOptions.initialUrl || '';
    urlInput.placeholder = 'https://example.com';
    urlInput.className = 'w-full px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
    urlInput.required = true;
    
    inputDiv.appendChild(urlLabel);
    inputDiv.appendChild(urlInput);
    form.appendChild(inputDiv);
    
    // Кнопки в нижней части
    const buttonsDiv = document.createElement('div');
    buttonsDiv.className = 'flex justify-end gap-2';
    
    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm';
    cancelButton.textContent = 'Отмена';
    cancelButton.addEventListener('click', () => {
      console.log("[MODAL MANAGER] Нажата кнопка Отмена");
      this.closeExternalLinkModal();
    });
    
    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'px-4 py-2 bg-blue-600 dark:bg-blue-500 rounded text-white text-sm hover:bg-blue-700 dark:hover:bg-blue-600';
    submitButton.textContent = 'Добавить';
    
    buttonsDiv.appendChild(cancelButton);
    buttonsDiv.appendChild(submitButton);
    form.appendChild(buttonsDiv);
    
    // Собираем все вместе
    modalContent.appendChild(headerDiv);
    modalContent.appendChild(form);
    
    modalContainer.appendChild(modalContent);
    
    // Обработчик клавиши Escape
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log("[MODAL MANAGER] Нажата клавиша Escape");
        this.closeExternalLinkModal();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Сохраняем обработчик для последующего удаления
    modalContainer.dataset.keydownHandler = 'true';
    
    return modalContainer;
  }
  
  /**
   * Обновляет опции модального окна выбора документа и немедленно обновляет список документов, если окно открыто
   */
  public updateOptions(options: DocumentModalOptions): void {
    if (this.documentOptions) {
      console.log('[MODAL MANAGER] Обновляем опции модального окна. Загрузка:', options.isLoading, 'Элементов:', options.items?.length || 0, 'Поисковый запрос:', options.searchTerm);
      
      // Обновляем локальное состояние поискового запроса, если оно изменилось
      if (options.searchTerm !== undefined && options.searchTerm !== this.currentSearchTerm) {
        this.currentSearchTerm = options.searchTerm;
        console.log('[MODAL MANAGER] Обновлен локальный поисковый запрос:', this.currentSearchTerm);
        
        // Обновляем значение в поле ввода, если оно существует
        if (this.activeModal) {
          const searchInput = this.activeModal.querySelector<HTMLInputElement>('input[type="text"]');
          if (searchInput) {
            searchInput.value = this.currentSearchTerm;
          }
        }
      }
      
      this.documentOptions = options;
      
      // Немедленно обновляем контент, если модальное окно открыто
      if (this.activeModal) {
        const documentsContainer = this.activeModal.querySelector<HTMLDivElement>('#documents-container');
        if (documentsContainer) {
          this.updateDocumentsList(documentsContainer);
        }
      }
    }
  }
  
  /**
   * Обновляет список документов в контейнере
   */
  private updateDocumentsList(container: HTMLDivElement): void {
    if (!this.documentOptions) return;
    
    const { items, isLoading } = this.documentOptions;
    
    // Отладочный лог для проверки состояния документов
    console.log('[MODAL MANAGER] updateDocumentsList: isLoading =', isLoading, 'items =', items?.length || 0);
    
    // Если загрузка, показываем спиннер
    if (isLoading) {
      console.log('[MODAL MANAGER] Отображаем статус загрузки');
      container.innerHTML = `
        <div class="loading-indicator p-6 text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
          <span>Загрузка документов...</span>
        </div>`;
      return;
    }
    
    // Если нет элементов для отображения
    if (!items || items.length === 0) {
      console.log('[MODAL MANAGER] Нет элементов для отображения');
      container.innerHTML = `
        <div class="empty-message p-6 text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Нет доступных документов</span>
        </div>`;
      return;
    }
    
    console.log('[MODAL MANAGER] Обновляем список документов:', items.length, 'элементов');
    
    // Используем локальное значение для фильтрации
    const searchTerm = this.currentSearchTerm.toLowerCase();
    
    // Фильтруем элементы прямо здесь для визуального отображения
    const filteredItems = searchTerm ? 
      items.filter(item => item.name.toLowerCase().includes(searchTerm)) : 
      items;
    
    console.log('[MODAL MANAGER] После фильтрации осталось:', filteredItems.length, 'элементов. Запрос:', searchTerm);
    
    // Формируем HTML элементов
    const documentsHTML = filteredItems.map(item => `
      <div class="document-item flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-200 dark:border-gray-700 transition-colors" data-id="${item.id}" data-title="${item.name}">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">${item.name}</p>
          ${item.parentId ? `<p class="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">${item.parentId}</p>` : ''}
        </div>
      </div>
    `).join('');

    container.innerHTML = documentsHTML;

    // Если нет отфильтрованных элементов, показываем сообщение
    if (filteredItems.length === 0 && searchTerm) {
      container.innerHTML = `
        <div class="empty-message p-6 text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span>Нет документов, соответствующих запросу</span>
        </div>`;
    }

    // Добавляем обработчики клика на элементы
    const documentItems = container.querySelectorAll<HTMLDivElement>('.document-item');
    documentItems.forEach(item => {
      item.addEventListener('click', this.handleDocumentItemClick.bind(this));
    });
  }
  
  // Обработчик клика на элемент документа
  private handleDocumentItemClick(event: Event): void {
    if (!this.documentOptions) {
      console.error('[MODAL MANAGER] Не найдены опции документа при клике');
      return;
    }
    
    const target = event.currentTarget as HTMLDivElement;
    const itemId = target.getAttribute('data-id');
    const itemTitle = target.getAttribute('data-title');
    
    console.log('[MODAL MANAGER] Клик по документу:', itemId, itemTitle);
    
    if (itemId) {
      // Находим выбранный элемент в списке
      const selectedItem = this.documentOptions.items.find(item => item.id === itemId);
      
      if (selectedItem) {
        console.log('[MODAL MANAGER] Выбран документ:', selectedItem.name, 'ID:', selectedItem.id);
        
        try {
          // Сначала закрываем модальное окно, чтобы не мешало
          this.closeModal();
          
          // Затем вызываем колбэк
          console.log('[MODAL MANAGER] Вызываем onItemSelect с объектом:', JSON.stringify(selectedItem));
          if (typeof this.documentOptions.onItemSelect === 'function') {
            this.documentOptions.onItemSelect(selectedItem);
            console.log('[MODAL MANAGER] onItemSelect выполнен успешно');
          } else {
            console.error('[MODAL MANAGER] onItemSelect не является функцией!');
          }
        } catch (error) {
          console.error('[MODAL MANAGER] Ошибка при обработке выбора документа:', error);
        }
      } else {
        console.error('[MODAL MANAGER] Выбранный документ не найден в списке');
        this.closeModal();
      }
    } else {
      console.error('[MODAL MANAGER] Не удалось получить ID документа');
      this.closeModal();
    }
  }
  
  // Запуск интервала для обновления содержимого модального окна
  private startUpdateInterval(): void {
    // Сразу очищаем старый интервал, если он был
    this.stopUpdateInterval();
    
    console.log('[MODAL MANAGER] Запуск интервала обновления списка документов');
    this.updateIntervalId = window.setInterval(() => {
      if (this.activeModal) {
        const documentsContainer = this.activeModal.querySelector<HTMLDivElement>('#documents-container');
        if (documentsContainer) {
          console.log('[MODAL MANAGER] Интервал обновления: isLoading =', this.documentOptions?.isLoading, 'items =', this.documentOptions?.items?.length);
          this.updateDocumentsList(documentsContainer);
        } else {
          console.warn('[MODAL MANAGER] Контейнер документов не найден');
        }
      } else {
        console.log('[MODAL MANAGER] Модальное окно закрыто, останавливаем интервал');
        this.stopUpdateInterval();
      }
    }, 500) as unknown as number;
  }
  
  // Остановка интервала обновления
  private stopUpdateInterval(): void {
    if (this.updateIntervalId !== null) {
      clearInterval(this.updateIntervalId);
      this.updateIntervalId = null;
    }
  }
  
  // Закрыть модальное окно документов и уведомить о закрытии
  private closeAndNotify(): void {
    if (this.documentOptions) {
      const { onClose } = this.documentOptions;
      this.closeModal();
      onClose();
    } else if (this.externalLinkOptions) {
      this.closeExternalLinkModal();
    }
  }
  
  // Закрыть модальное окно внешней ссылки и уведомить о закрытии
  private closeExternalLinkModal(): void {
    if (this.externalLinkOptions) {
      const { onClose } = this.externalLinkOptions;
      this.closeModal();
      onClose();
    }
  }
}

export const modalManager = ModalManager.getInstance(); 